import { Controller } from '@hotwired/stimulus';
import Choices from 'choices.js';

export default class extends Controller {
  static values = {
    searchEnabled: Boolean,
    remoteSearchUrl: String,
    noChoicesText: String,
    noResultsText: String,
    hideRemoveItemButton: Boolean,
    renderChoiceLimit: Number,
    autoClose: Boolean,
    // Match the typed text as a plain substring instead of the fuzzy default.
    exactSearch: Boolean
  };

  connect() {
    if (this.element.dataset.choice !== 'active') {
      const noResultsText = this.noResultsTextValue || 'No results found';
      const noChoicesText = this.noChoicesTextValue || 'No choices to choose from';
      let choices;

      choices = new Choices(this.element, {
        allowHTML: true,
        silent: true,
        searchEnabled: this.searchEnabledValue,
        searchChoices: !this.remoteSearchUrlValue,
        noResultsText,
        // A remote search never counts as searching for Choices, so an empty
        // answer to a typed query has to be told apart from an empty list.
        noChoicesText: () => (this.remoteSearchUrlValue && choices?.input.value ? noResultsText : noChoicesText),
        searchFloor: 1,
        searchResultLimit: 10,
        shouldSort: false,
        maxItemCount: -1,
        renderChoiceLimit: this.renderChoiceLimitValue || -1,
        removeItems: true,
        delimiter: ',',
        removeItemButton: !this.hideRemoveItemButtonValue,
        duplicateItemsAllowed: false,
        addItems: true,
        fuseOptions: this.exactSearchValue ? { threshold: 0, ignoreLocation: true } : { threshold: 0.2 }
      });

      if(this.autoCloseValue) {
        choices.passedElement.element.addEventListener(
          'addItem',
          function() {
            choices.hideDropdown();
          },
          false
        );
      }

      if (this.remoteSearchUrlValue) {
        const lookupDelay = 300;
        let lookupTimeout = null;
        let latestLookup = 0;

        this.element.addEventListener('search', (event) => {
          clearTimeout(lookupTimeout);

          lookupTimeout = setTimeout(async () => {
            const lookup = ++latestLookup;

            try {
              const response = await fetch(this.remoteSearchUrlValue + encodeURIComponent(event.detail.value));
              const data = await response.json();

              // A slow answer to an earlier query must not replace the current results.
              if (lookup !== latestLookup) return;

              // Handing Choices the results in one step, rather than a fetcher, spares it
              // the loading state that disables the widget and drops the focus: the caret
              // stays in the search box, the first result is highlighted, Enter picks it.
              choices.setChoices(data, 'value', 'label', true);
            } catch {
              // The list keeps what it had; the next keystroke asks again.
            }
          }, lookupDelay);
        });
      }
    }
  }
}
